import { useState } from 'react';

const faqs = [
  {
    question: 'What addiction treatment programs do you offer?',
    answer:
      'We offer a full range of evidence-based addiction treatment programs including Methadone Maintenance Therapy (MMT), Suboxone (buprenorphine/naloxone) treatment, Kadian, alcohol use disorder programs, and smoking cessation support. Each plan is personalized to your needs.',
  },
  {
    question: 'Do I need a referral to start treatment?',
    answer:
      'No referral is required. You can walk in or call us directly to begin your assessment. Our clinical team will guide you through the intake process and help you start the right program as quickly as possible.',
  },
  {
    question: 'What pharmacy services are available at your clinic?',
    answer:
      'Our full-service pharmacy provides prescription dispensing, medication counselling, blister packaging for complex regimens, compounding services, OTC products, and direct billing to most insurance providers.',
  },
  {
    question: 'How does Methadone treatment work?',
    answer:
      'Methadone is dispensed daily under medical supervision at our clinic. Over time, as stability is demonstrated, patients may qualify for take-home carries. Regular medical check-ins and urine drug screens are part of the program to ensure safety and progress.',
  },
  {
    question: 'Is Suboxone available as a take-home medication?',
    answer:
      'Yes. Suboxone treatment is designed for greater flexibility. Depending on your stability and treatment plan, take-home doses may be available sooner than with Methadone, allowing you to maintain your daily routine while in recovery.',
  },
  {
    question: 'What is the TSCP network?',
    answer:
      'Toronto Safe Clinics & Pharmacies (TSCP) is a trusted network of harm-reduction focused clinics and pharmacies across Toronto. Our clinic is a proud member, ensuring patients receive consistent, compassionate, and high-quality care.',
  },
];

export default function Care365CTA() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 md:py-24 bg-background-50 w-full overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-start">

          {/* LEFT — FAQ Accordion */}
          <div className="w-full lg:w-3/5 order-2 lg:order-1">
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className={`border rounded-xl overflow-hidden transition-all duration-300 cursor-pointer ${
                    openIndex === index
                      ? 'border-primary-200 bg-primary-50/40'
                      : 'border-background-100 bg-background-50 hover:border-background-200'
                  }`}
                  onClick={() => toggle(index)}
                >
                  <button
                    className="w-full flex items-center justify-between px-5 py-4 text-left gap-4 cursor-pointer"
                    aria-expanded={openIndex === index}
                  >
                    <span
                      className={`text-sm sm:text-base font-semibold leading-snug transition-colors duration-200 ${
                        openIndex === index ? 'text-primary-600' : 'text-foreground-800'
                      }`}
                    >
                      {faq.question}
                    </span>
                    <div
                      className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${
                        openIndex === index
                          ? 'bg-primary-500 text-background-50 rotate-180'
                          : 'bg-background-100 text-foreground-500'
                      }`}
                    >
                      <i className="ri-arrow-down-s-line text-base"></i>
                    </div>
                  </button>

                  <div
                    className={`px-5 transition-all duration-300 ease-in-out overflow-hidden ${
                      openIndex === index ? 'max-h-60 pb-5 opacity-100' : 'max-h-0 pb-0 opacity-0'
                    }`}
                  >
                    <p className="text-sm sm:text-base text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — Heading & Description */}
          <div className="w-full lg:w-2/5 order-1 lg:order-2 lg:sticky lg:top-28">
            <div className="inline-flex items-center gap-2 bg-primary-100 px-4 py-2 rounded-full mb-5">
              <i className="ri-question-answer-line text-primary-600 text-sm"></i>
              <span className="text-primary-700 text-xs font-semibold uppercase tracking-wide">
                Common Questions
              </span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-5">
              Everything You <br />
              <span className="text-primary-600">Need to Know</span>
            </h2>

            <p className="text-foreground-500 text-base leading-relaxed mb-8">
              We know starting your treatment journey can feel overwhelming. These are the most common questions our patients ask — we want you to feel informed, comfortable, and confident every step of the way.
            </p>

            <div className="space-y-4 mb-8">
              {[
                { icon: 'ri-shield-check-line', label: 'Judgment-Free Care' },
                { icon: 'ri-user-heart-line', label: 'Personalized Treatment Plans' },
                { icon: 'ri-time-line', label: 'Same-Day Assessments Available' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-primary-600 text-base`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-700">{item.label}</span>
                </div>
              ))}
            </div>

            <a
              href="/addiction-services"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-lg text-sm font-bold transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              <span>Explore Our Programs</span>
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>

        </div>
      </div>
    </section>
  );
}
